import { gsap } from "gsap";
import { Container } from "pixi.js";

import { EventsManager, GameEvents } from "../../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../../abstracts/flow/AbstractGameMode";
import { AbstractBitmapText } from "../../../../abstracts/graphics/AbstractBitmapText";
import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { fitDisplayObjectToWidth } from "../../../../abstracts/utils/GraphicUtils";
import {
    fadeOutBackgroundMusic,
    playSound,
    stopSound,
} from "../../../../abstracts/utils/SoundUtils";
import { DDD } from "../../../DDD";
import { DDDFontsNames, DDDSoundNames, DDDSpineNames } from "../../../assetsLoader/DDDAssetsNames";
import { symbolConfig, xNudgeFontSize } from "../../../config/DDDConfig";
import { DDDSymbolNames } from "../../../types/DDDEnums";
import { DDDInitData } from "../../../types/DDDInterfaces";
import { respinMusic } from "../../Sound/DDDSoundController";
import { DDDXGodSymbol } from "./DDDXGodSymbol";

export class DDDXNudgeSymbol extends DDDXGodSymbol {
    private nudgeSteps: number = 0;
    private nudgeDirection: string = `none`;
    private currentMultiplier: number = 0;
    private multiplierText: AbstractBitmapText;
    private isHanged: boolean = false;
    private winSpineName: string = `Win_unhanged`;
    private multiplierContainer: Container;

    public isSticky: boolean = false;

    override set isSpinning(value: boolean) {
        this._isSpinning = value;

        if (this.mainSpine && value) {
            this.mainSpine.reset();
            this.mainSpine.state.setAnimation(0, `Spin`, false);
        }
    }

    protected generateSymbol(): void {
        this.mainSpine = new AbstractSpine(DDDSpineNames.XNUDGE);
        this.mainSpine.name = `${this.name}`;
        this.mainSpine.scale.set(0.5);
        this.mainSpine.x = symbolConfig.symbolWidth / 2;
        switch (this.name) {
            case DDDSymbolNames.XN_2:
                this.mainSpine.y = -symbolConfig.symbolHeight;
                break;

            case DDDSymbolNames.XN_3:
                this.mainSpine.y = -symbolConfig.symbolHeight * 2;
                break;

            default:
                this.mainSpine.y = 0;
                break;
        }
        this.mainSpine.y += 283;
        this.mainSpine.alpha = 1;

        this.multiplierText = new AbstractBitmapText({
            text: `x${1}`,
            style: {
                fontName: DDDFontsNames.DEFAULT,
                fontSize: xNudgeFontSize,
                // letterSpacing: 20,
            },
        });

        this.multiplierContainer = new Container();
        this.multiplierContainer.name = `multiplierTextContainer`;
        this.multiplierText.pivot.y = 25;
        this.multiplierText.name = `multiplierText`;
        this.multiplierText.alpha = 0;

        this.multiplierContainer.addChild(this.multiplierText);
        this.mainSpine.addChild(this.multiplierContainer);

        this.addChild(this.mainSpine);
    }

    public getWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        if (this.visible) {
            return this.mainSpine.getSpineTween(`${this.winSpineName}`);
        }
        return gsap.to({}, { duration: 0 });
    }

    public override getLandingAnimation(): gsap.core.Tween | gsap.core.Timeline {
        if (this.visible) {
            this.multiplierText.alpha = 1;
            this.currentMultiplier = 1;
            this.multiplierText.updateValue(`x${this.currentMultiplier}`);
            // this.mainSpine.addChild(this.multiplierContainer);
            return this.mainSpine.getSpineTween(`landing`, false, () => {
                playSound({ name: DDDSoundNames.XNUDGE_LAND_PARTIAL });
                EventsManager.trigger(GameEvents.UPDATE_GLOBAL_MULTIPLIER, 1);
            });
        }
        return gsap.to({}, { duration: 0 });
    }

    public getNudgeAnimation(
        isLocked: boolean = false,
        isWin: boolean = false
    ): gsap.core.Tween | gsap.core.Timeline {
        const tl = gsap.timeline();
        switch (this.name) {
            case DDDSymbolNames.XN_2:
                if (this.positionInArray === 1) {
                    this.nudgeSteps = 1;
                    this.nudgeDirection = `down`;
                }
                break;

            case DDDSymbolNames.XN_3:
                if (this.positionInArray === 1) {
                    this.nudgeSteps = 2;
                    this.nudgeDirection = `down`;
                }
                break;

            default:
                if (this.positionInArray === 1) {
                    this.nudgeSteps = 0;
                    this.nudgeDirection = `none`;
                }
                if (this.positionInArray === 2) {
                    this.nudgeSteps = 1;
                    this.nudgeDirection = `up`;
                }
                if (this.positionInArray === 3) {
                    this.nudgeSteps = 2;
                    this.nudgeDirection = `up`;
                }
                break;
        }

        // nudge animation
        if (!this.isSticky) {
            this.currentMultiplier = 1;

            if (this.nudgeSteps > 0) {
                if (isWin) {
                    for (let i = 1; i <= this.nudgeSteps; i++) {
                        this.currentMultiplier++;
                        const stepY =
                            this.nudgeDirection === `down`
                                ? this.mainSpine.y + symbolConfig.symbolHeight * i
                                : this.nudgeDirection === `up`
                                ? this.mainSpine.y - symbolConfig.symbolHeight * i
                                : this.mainSpine.y;
                        tl.add(
                            this.mainSpine.getSpineTween(`xNudge`, false, () => {
                                const nudgeSound =
                                    i === 1
                                        ? DDDSoundNames.XNUDGE_NUDGE_1
                                        : DDDSoundNames.XNUDGE_NUDGE_2;
                                playSound({ name: nudgeSound });
                            })
                        );

                        this.nudgeDirection !== `none` &&
                            tl.add(
                                gsap.to(this.mainSpine, {
                                    y: stepY,
                                    ease: `M0,0 C0.1,0.6 0.3,1.1 0.5,1 0.7,0.9 0.9,1 1,1`,
                                }),
                                `<`
                            );
                        tl.add(this.multiUpdate(this.currentMultiplier), `>-=0.25`);
                        tl.call(
                            () => EventsManager.trigger(GameEvents.UPDATE_GLOBAL_MULTIPLIER, 1),
                            undefined,
                            `<`
                        );
                    }
                } else {
                    const stepY =
                        this.nudgeDirection === `down`
                            ? this.mainSpine.y + symbolConfig.symbolHeight * this.nudgeSteps
                            : this.nudgeDirection === `up`
                            ? this.mainSpine.y - symbolConfig.symbolHeight * this.nudgeSteps
                            : this.mainSpine.y;
                    tl.add(
                        this.mainSpine.getSpineTween(`xNudge`, false, () => {
                            playSound({ name: DDDSoundNames.XNUDGE_NUDGE_1 });
                        })
                    );
                    this.nudgeDirection !== `none` &&
                        tl.add(
                            gsap.to(this.mainSpine, {
                                y: stepY,
                                ease: `M0,0 C0.1,0.6 0.3,1.1 0.5,1 0.7,0.9 0.9,1 1,1`,
                            }),
                            `<`
                        );
                    this.currentMultiplier += this.nudgeSteps;
                    tl.add(this.multiUpdate(this.currentMultiplier), `>-=0.25`);
                    tl.add(
                        () =>
                            EventsManager.trigger(
                                GameEvents.UPDATE_GLOBAL_MULTIPLIER,
                                this.nudgeSteps
                            ),
                        `<`
                    );
                }
            }

            if (isLocked) {
                this.isSticky = isLocked;
            }
        } else {
            if (!isLocked) {
                this.isSticky = false;
            }
        }

        !isWin && tl.timeScale(3);

        return tl;
    }

    public getNoWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        return super.getNoWinAnimation();
    }

    public xNudgeAllAnimationsFlow(
        xNudgeMultipliersCurrent: number,
        reelIndex: number
    ): gsap.core.Timeline {
        const tl = gsap.timeline();
        const isTease = DDD.model.gameData.teaseXNudge[reelIndex];
        const divider =
            DDD.model.gameData.mode === AbstractGameMode.SUPER_FREESPIN ||
            (DDD.model.gameData.mode === AbstractGameMode.FREESPIN &&
                DDD.model.gameData.nextMode === AbstractGameMode.SUPER_FREESPIN)
                ? 3
                : 2;
        const currentDataMulti = DDD.model.gameData.xNudgeMultipliersCurrent[reelIndex];
        const nextDataMulti = DDD.model.gameData.xNudgeMultipliersNext[reelIndex];

        if (this.isSticky && !this.isHanged) {
            tl.add(this.hangAnimation());
            tl.add(this.stickyAnimation());
            this.isHanged = true;
            this.winSpineName = `Win_hanged_sticky`;

            if (nextDataMulti > this.currentMultiplier) {
                tl.add(this.mainSpine.getSpineTween(`sticky_tug`));
                tl.add(this.multiUpdate(nextDataMulti), `<`);
                tl.add(() => {
                    EventsManager.trigger(
                        GameEvents.UPDATE_GLOBAL_MULTIPLIER,
                        nextDataMulti - currentDataMulti
                    );
                }, `<`);
                this.winSpineName = `Win_hanged_sticky`;
            }
        } else if (this.isSticky && this.isHanged) {
            if (nextDataMulti > this.currentMultiplier) {
                tl.add(this.mainSpine.getSpineTween(`sticky_tug`));
                tl.add(this.multiUpdate(nextDataMulti), `<`);
                tl.add(() => {
                    EventsManager.trigger(
                        GameEvents.UPDATE_GLOBAL_MULTIPLIER,
                        nextDataMulti - currentDataMulti
                    );
                }, `<`);
                this.winSpineName = `Win_hanged_sticky`;
            }
        } else if (!this.isSticky && !this.isHanged) {
            if (isTease) {
                tl.add(this.hangAnimation());
                tl.add(this.fallAnimation(false, reelIndex));
                this.winSpineName = `win_empty`;
            }
        } else if (!this.isSticky && this.isHanged) {
            if (xNudgeMultipliersCurrent * divider < 999) {
                tl.add(this.mainSpine.getSpineTween(`sticky_tug`));
                tl.add(this.multiUpdate(xNudgeMultipliersCurrent), `<`);
                tl.add(() => {
                    EventsManager.trigger(
                        GameEvents.UPDATE_GLOBAL_MULTIPLIER,
                        nextDataMulti - currentDataMulti
                    );
                }, `<`);
            }

            tl.add(this.fallAnimation(true, reelIndex));
            if (xNudgeMultipliersCurrent * divider < 999) {
                tl.add(this.multiUpdate(nextDataMulti / divider), `<`);
                tl.add(() => {
                    EventsManager.trigger(
                        GameEvents.UPDATE_GLOBAL_MULTIPLIER,
                        -(nextDataMulti - currentDataMulti)
                    );
                }, `<`);
            }

            this.isHanged = false;
            this.winSpineName = `sticky_win_empty`;
        }

        return tl;
    }

    protected hangAnimation(): gsap.core.Tween {
        return this.mainSpine.getSpineTween(`xNudge_hang`, false, () => {
            playSound({ name: DDDSoundNames.XNUDGE_HANG });
        });
    }

    protected stickyAnimation(): gsap.core.Tween {
        const data = DDD.model.data.isRestoreState ? DDD.model.initData : DDD.model.gameData;
        return this.mainSpine.getSpineTween(`sticky`, false, () => {
            playSound({ name: DDDSoundNames.XNUDGE_STICKY });

            data.mode !== AbstractGameMode.RESPIN &&
                EventsManager.trigger(GameEvents.RESPIN_FRAME, true);

            if (
                data.mode === AbstractGameMode.NORMAL &&
                data.nextMode === AbstractGameMode.RESPIN &&
                !respinMusic.isPlaying
            ) {
                fadeOutBackgroundMusic();
                playSound({ name: DDDSoundNames.XNUDGE_RESPIN_MUSIC, volume: 1, repeat: 999 });

                respinMusic.isPlaying = true;
            }
        });
    }

    protected fallAnimation(isSticky: boolean = false, reelIndex: number): gsap.core.Tween {
        if (isSticky) {
            return this.mainSpine.getSpineTween(`sticky_fall`, false, () => {
                playSound({ name: DDDSoundNames.XNUDGE_FALL });

                const lastNudgeIndex = DDD.model.gameData.xNudgeMultipliersCurrent.reduceRight(
                    (last, value, index) => {
                        return last === -1 && value !== -1 ? index : last;
                    },
                    -1
                );

                if (
                    reelIndex === lastNudgeIndex &&
                    DDD.model.gameData.nextMode !== AbstractGameMode.RESPIN
                ) {
                    stopSound({ name: DDDSoundNames.XNUDGE_RESPIN_MUSIC });
                    playSound({ name: DDDSoundNames.XNUDGE_RESPIN_END });
                    respinMusic.isPlaying = false;
                    EventsManager.trigger(GameEvents.RESPIN_FRAME, false);
                }
            });
        }
        return this.mainSpine.getSpineTween(`xNudge_fall`, false, () => {
            playSound({ name: DDDSoundNames.XNUDGE_FALL });
        });
    }

    private multiUpdate(multiplier: number): gsap.core.Tween {
        return gsap.to(this.multiplierText, {
            duration: 1,
            onStart: () => {
                this.multiplierText.updateValue(`x${multiplier}`);
                fitDisplayObjectToWidth(this.multiplierContainer, 260);
                this.currentMultiplier = multiplier;
                playSound({ name: DDDSoundNames.XNUDGE_MULTI_UPDATE });
            },
        });
    }

    public override reset(): void {
        // super.reset();

        switch (this.name) {
            case DDDSymbolNames.XN_2:
                this.mainSpine.y = -symbolConfig.symbolHeight;
                break;
            case DDDSymbolNames.XN_3:
                this.mainSpine.y = -symbolConfig.symbolHeight * 2;
                break;
            default:
                this.mainSpine.y = 0;
                break;
        }
        this.mainSpine.y += 278;

        this.winSpineName = `Win_unhanged`;
        if (this.currentMultiplier > 0 && !this.isSticky) {
            EventsManager.trigger(GameEvents.UPDATE_GLOBAL_MULTIPLIER, -this.currentMultiplier);
        }
        this.currentMultiplier = 0;
        this.multiplierText.updateValue(`x${this.currentMultiplier}`);
        this.multiplierContainer.scale.set(1);
        this.mainSpine.reset();
        this.mainSpine.getSpineTween(`Normal`);
        this.alpha = 1;
        this.multiplierText.alpha = 0;

        this.visible = true;
    }

    public override resetOnStartSpin() {
        gsap.killTweensOf(this.mainSpine);
        this.alpha = 1;
    }

    protected override resetMainSpine(): void {}

    public restore(iniData: DDDInitData, reelIndex: number) {
        const tl = gsap.timeline();
        if (iniData.stickyXNudge[reelIndex]) {
            this.isHanged = true;
            this.isSticky = true;
            tl.add(this.mainSpine.getSpineTween(`sticky`));
            this.winSpineName = `Win_hanged_sticky`;

            fadeOutBackgroundMusic();
            playSound({ name: DDDSoundNames.XNUDGE_RESPIN_MUSIC, volume: 1, repeat: 999 });

            respinMusic.isPlaying = true;

            EventsManager.trigger(GameEvents.RESPIN_FRAME, true);
            this.multiplierText.alpha = 1;
            this.multiUpdate(iniData.xNudgeMultipliersNext[reelIndex]);
            EventsManager.trigger(
                GameEvents.UPDATE_GLOBAL_MULTIPLIER,
                iniData.xNudgeMultipliersNext[reelIndex]
            );
        }
        return tl;
    }
}
